import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { AccountFilterMenu } from '@/features/conversations/AccountFilterMenu';
import { ConversationList } from '@/features/conversations/ConversationList';
import { useUIStore } from '@/store/ui';

interface ConversationColumnProps {
  /** Whether the account rail (Layout's first, collapsible panel) is currently
   *  collapsed. Drives the toggle button's icon + aria-label. Optional so the
   *  column can still be rendered standalone (e.g. in unit tests) — defaults to
   *  the rail being shown. */
  accountsCollapsed?: boolean;
  /** Toggle the account rail open/closed. Owned by `Layout` (it holds the
   *  PanelGroup + the panel ref); the button just calls it. Optional for the
   *  same standalone-render reason. */
  onToggleAccounts?: () => void;
}

/**
 * Middle column of the console shell — the conversation inbox.
 *
 *   - `selectedAccountId === null` (the console's default state on
 *     sign-in) shows the aggregate "所有账号" queue across every account.
 *   - A UUID scopes the queue to that one account.
 *
 * Either way `<ConversationList>` always mounts — the all-accounts view removed the old "pick
 * an account" placeholder, since `null` is no longer "nothing selected".
 * `key` is `selectedAccountId` (falling back to the literal `'all'`) so
 * switching between accounts — or to/from the aggregate view — fully
 * reinitialises the virtualizer (no stale rows, no stale scroll offset).
 *
 * The column owns its own header strip; the Layout grid cell just mounts
 * `<ConversationColumn />` (passing the account-rail toggle props) without
 * extra wrapping.
 *
 * HEADER (left -> right): the account-rail collapse toggle, the shared
 * `BrandLogo` mark + "iCGChat" wordmark, then the "Conversations" section
 * title. The brand lives here — not just in `AccountSidebar` — so the console
 * keeps its identity when the account rail is collapsed to zero width. The
 * mark is the `'sm'` size so it, the wordmark and the title share one
 * uncramped row.
 */
export function ConversationColumn({
  accountsCollapsed = false,
  onToggleAccounts,
}: ConversationColumnProps = {}): React.JSX.Element {
  const selectedAccountId = useUIStore((s) => s.selectedAccountId);

  return (
    // `conv-column` makes the section a size container so the header can
    // demote its lower-priority elements by CONTAINER width — the panel is
    // user-draggable, so viewport breakpoints can't see the real width. The
    // demotion tiers + the never-clip-the-trigger budget live in index.css.
    <section className="conv-column neu-panel-mid relative z-10 flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center gap-2.5 px-5 pb-1 pt-4">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={accountsCollapsed ? '显示账号栏' : '隐藏账号栏'}
          aria-pressed={accountsCollapsed}
          className="size-8 shrink-0 rounded-[10px] shadow-neu-raised-sm hover:shadow-neu-raised active:shadow-neu-inset-press"
          onClick={onToggleAccounts}
        >
          {accountsCollapsed ? (
            <PanelLeftOpen className="size-4" />
          ) : (
            <PanelLeftClose className="size-4" />
          )}
        </Button>
        {/* Brand + hairline are the header's lowest-priority elements: the
            wordmark + hairline hide below 30rem container width, the tile
            below 18.75rem (index.css `.conv-header-brand` rules), so the
            account-filter trigger never clips at narrow panel widths. */}
        <BrandLogo size="sm" className="conv-header-brand" />
        {/* Hairline between the brand and the section title so the row reads as
            "[brand] · Conversations" rather than one run-on label. */}
        <span
          aria-hidden="true"
          className="conv-header-hairline h-5 w-px shrink-0 bg-border/60"
        />
        {/* min-w-0 + truncate: the title is the header's designated slack
            absorber — it shrinks (to an ellipsis if needed) before anything
            can push the shrink-0 filter trigger past the panel edge. */}
        <span className="min-w-0 truncate text-[17px] font-extrabold">
          Conversations
        </span>
        {/* Account-scope filter — `ml-auto` pushes it to the right edge. Works
            (and is the point) when the account rail is collapsed, since this
            header is always visible. */}
        <AccountFilterMenu />
      </header>
      <div className="min-h-0 flex-1 overflow-hidden">
        <ConversationList
          key={selectedAccountId ?? 'all'}
          accountId={selectedAccountId}
        />
      </div>
    </section>
  );
}
